import React, { FC } from 'react';

import {
  Box,
  Button,
  Divider,
  Flex,
  Skeleton,
  Stack,
  Text,
} from '@chakra-ui/react';

import { scrollbarStyles } from 'theme';
import { Link as RouterLink } from 'react-router-dom';

import { AnimatePresence, motion } from 'framer-motion';

import { ReportInReviewProps } from './ReportInReview.props';
import { convertUTCToLocal } from 'utils/dateUtils';
import { InventoryReportStatusEnum } from 'types/report';
import { useInventoryReports } from 'hooks/useInventoryReports';
import { Tabs } from 'routes/portal/HarvestSummary/HarvestSummary.props';
import { calHarvestRate } from 'utils/harvestSummaryUtils';
import { selectors } from 'store/slices/washhub';

const MotionFlex = motion(Flex);

const getMotionProps = (index: number) => ({
  initial: {
    opacity: 0,
    scale: 0.5,
  },
  animate: {
    opacity: 1,
    scale: 1,
  },
  transition: {
    delay: 0.03 * index,
    ease: 'easeInOut',
    duration: 0.3,
  },
  exit: {
    opacity: 0,
    scale: 0.5,
  },
});

const ReportInReviewView: FC<ReportInReviewProps> = (props) => {
  const { reports: data, total, isFetching } = useInventoryReports({
    status: InventoryReportStatusEnum.IN_REVIEW,
  });

  const isEmpty = data.length === 0;

  const reports = data.slice(0, 3);

  const washHubId = selectors.useSelectedWashHubId();
  const navigateUrl = `/harvest-summary?washHubId=${washHubId}&tabIdx=${Tabs.IN_REVIEW}`;

  return (
    <Flex
      p="10px"
      pl="20px"
      minW="300px"
      h="100%"
      bg="#fff"
      minH="480px"
      maxH="480px"
      overflowY="auto"
      css={scrollbarStyles}
      flex={1}
      gap="20px"
      direction="column"
      borderRadius="10px"
      borderWidth="1px">
      <Flex justifyContent="space-between">
        <Text fontSize="22px" fontWeight="600" color="primary.200">
          Reports in Review ({total ?? 0})
        </Text>
        <Button
          as={RouterLink}
          to={navigateUrl}
          colorScheme="teal"
          backgroundColor="transparent"
          variant="link">
          View All
        </Button>
      </Flex>

      {isFetching ? (
        <>
          <Skeleton height="150px" />
          <Skeleton height="150px" />
          <Skeleton height="150px" />
        </>
      ) : isEmpty ? (
        <Text ml="10px">No reports for the week</Text>
      ) : (
        <AnimatePresence exitBeforeEnter>
          {reports.map((report) => {
            const harvestRate = calHarvestRate(report);
            return (
              <MotionFlex
                bg="#fff"
                key={report._id}
                boxShadow="0 0 10px 0 rgba(0,0,0,0.1)"
                p="10px"
                pr="20px"
                pb="20px"
                borderRadius="10px"
                direction="column"
                // cursor="pointer"
                {...getMotionProps(0)}>
                <Flex gap="10px" align="center">
                  <Box h="15px" w="15px" borderRadius="15px" bg="#00BF13" />
                  <Text noOfLines={1}>{report?.site?.name || ''}</Text>
                </Flex>
                <Text opacity="0.6" ml="25px" fontSize="14px">
                  Report No : {report.reportNo || '-'}
                </Text>
                <Divider my="5px" borderColor="#ccc" />
                <Stack mt="5px" ml="25px" spacing={0} fontSize="14px">
                  <Text noOfLines={1}>{report?.name || ''}</Text>

                  <Text>
                    Date:{' '}
                    {convertUTCToLocal(report?.startDate, true, true) || '-'}
                    {' - '}
                    {convertUTCToLocal(report?.endDate, true, true) || '-'}
                  </Text>
                  <Text>Harvest Rate: {harvestRate}%</Text>
                </Stack>
              </MotionFlex>
            );
          })}
        </AnimatePresence>
      )}
    </Flex>
  );
};

export default ReportInReviewView;
